import React, {useCallback, useMemo} from 'react';
import {Link} from 'react-router-dom';

import {Skeleton, SkeletonItem} from 'src/components/skeleton';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';


import type {ProfileRecord} from 'src/openapi/admin/models/ProfileRecord';

type Props = {
    isLoading: boolean,
    disabled?: boolean,
    role: ProfileRecord | null,
    isDefaultAdminPermissions: boolean,
    onReset: () => void,
};

const ResetAdminPermissionsSkeleton = () => (
    <Group>
        <SkeletonItem width={156} height={32}/>
        <SkeletonItem width={400} height={18}/>
    </Group>
);

export const ResetAdminPermissions = (props: Props) => {
    const {
        isLoading,
        disabled,
        role,
        isDefaultAdminPermissions,
        onReset,
    } = props;

    const {userRoleUpdate} = useEventTracking();

    const onResetClick = useCallback(() => {
        onReset();
        userRoleUpdate({
            parameterName: 'Use default settings',
            targetUserId: role?.profileId || -1,
        });
    }, [onReset, role, userRoleUpdate]);

    const description = useMemo(() => {
        if (!role) {
            return 'User is not assigned to any role.';
        }

        const roleLink = (
            <Link to={`/roles/${role?.profileId}`}>
                <EllipsisTextHint title={role?.profileName}>{role?.profileName}</EllipsisTextHint>
            </Link>
        );

        if (isDefaultAdminPermissions) {
            return (
                <Group wrap="wrap" gap="xs">
                    Currently using default settings for <b style={{maxWidth: 200}}>{roleLink}</b> role.
                </Group>
            );
        }

        return (
            <Group wrap="wrap" gap="xs">
                <b>Currently using custom settings*.</b>
                Reset to defaults for the <b style={{maxWidth: 200}}>{roleLink}</b> role.
            </Group>
        );
    }, [role, isDefaultAdminPermissions]);

    return (
        <Skeleton shown={isLoading} view={<ResetAdminPermissionsSkeleton/>}>
            <Group>
                <Button
                    variant="Type.SECONDARY"
                    disabled={isDefaultAdminPermissions || !role || disabled}
                    onClick={onResetClick}>
                    Use default settings
                </Button>
                {description}
            </Group>
        </Skeleton>
    );
};
